import { apiClient } from './client';
import type { RequestOptions } from '@/types/api';
import type {
  Domain,
  DomainListResponse,
  DomainDetailResponse,
  CreateDomainRequest,
  CreateDomainResponse,
  UpdateDomainRequest,
  DomainFilters,
  DNSValidationResponse,
  DomainHeader,
  CreateHeaderRequest,
  CreateHeaderResponse,
  DomainNodeStatusResponse,
  SetupCDNNodeRequest,
  SetupCDNNodeResponse,
  ImportPleskCertificateRequest,
  ImportPleskCertificateResponse,
} from '@/types/domain';

/**
 * Domains API functions for the CDN Manager
 */
export const domainsApi = {
  /**
   * Fetch all domains with optional filters
   */
  async getAll(filters?: DomainFilters, options?: RequestOptions): Promise<DomainListResponse> {
    const params: Record<string, string> = {};

    if (filters?.node_id) {
      params.node_id = filters.node_id;
    }
    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.search) {
      params.search = filters.search;
    }

    const response = await apiClient.get<DomainListResponse>('/domains', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Fetch a single domain by ID with related entities
   */
  async getById(id: string): Promise<DomainDetailResponse> {
    const response = await apiClient.get<DomainDetailResponse>(`/domains/${id}`);
    return response.data;
  },

  /**
   * Create a new domain
   */
  async create(data: CreateDomainRequest): Promise<CreateDomainResponse> {
    const response = await apiClient.post<CreateDomainResponse>('/domains', data);
    return response.data;
  },

  /**
   * Update an existing domain
   */
  async update(id: string, data: UpdateDomainRequest): Promise<Domain> {
    const response = await apiClient.put<Domain>(`/domains/${id}`, data);
    return response.data;
  },

  /**
   * Delete a domain
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/domains/${id}`);
  },

  /**
   * Validate DNS configuration for a domain
   */
  async validateDNS(id: string): Promise<DNSValidationResponse> {
    const response = await apiClient.post<DNSValidationResponse>(`/domains/${id}/validate-dns`);
    return response.data;
  },

  /**
   * Fetch headers for a domain
   */
  async getHeaders(domainId: string): Promise<DomainHeader[]> {
    const response = await apiClient.get<{ headers: DomainHeader[] }>(`/domains/${domainId}/headers`);
    return response.data.headers || [];
  },

  /**
   * Add a custom header to a domain
   */
  async addHeader(domainId: string, data: CreateHeaderRequest): Promise<CreateHeaderResponse> {
    const response = await apiClient.post<CreateHeaderResponse>(`/domains/${domainId}/headers`, data);
    return response.data;
  },

  /**
   * Delete a header from a domain
   */
  async deleteHeader(domainId: string, headerId: string): Promise<void> {
    await apiClient.delete(`/domains/${domainId}/headers/${headerId}`);
  },

  /**
   * Fetch per-node deployment status for a domain
   */
  async getNodeStatus(domainId: string, options?: RequestOptions): Promise<DomainNodeStatusResponse> {
    const response = await apiClient.get<DomainNodeStatusResponse>(`/domains/${domainId}/node-status`, {
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Setup a CDN node for a domain (install certificate, configure nginx, add DNS)
   */
  async setupNode(domainId: string, data: SetupCDNNodeRequest): Promise<SetupCDNNodeResponse> {
    const response = await apiClient.post<SetupCDNNodeResponse>(`/domains/${domainId}/setup-node`, data);
    return response.data;
  },

  /**
   * Import a certificate from a Plesk node for a domain
   */
  async importPleskCertificate(
    domainId: string,
    data?: ImportPleskCertificateRequest
  ): Promise<ImportPleskCertificateResponse> {
    const response = await apiClient.post<ImportPleskCertificateResponse>(
      `/domains/${domainId}/import-plesk-certificate`,
      data || {}
    );
    return response.data;
  },
};
